import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import {
  Badge,
  Button,
  DialogFooter,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Building2, Loader2, ShieldCheck } from 'lucide-react';
import type { AgencyClinicResponse } from '../../clinician-manager/types';
import { type RoleAssignmentDialogState } from './useRoleAssignmentDialog';
import { AgencyRoleCard, ClinicRoleSection } from './rolePickers';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../../constants/featureFlags';
import { filterNonEmrAgencyRoles } from '../../../utils/rbacRoles';
import { useShowEmrUserManagement } from '../../../hooks/useShowEmrUserManagement';
import { type ClinicDisciplinesControl } from '../../clinician-manager/ClinicDisciplinesSection';

// ─── RoleAssignmentContent ───────────────────────────────────────────────────

export type RoleAssignmentHandle = {
  save: () => Promise<void>;
};

export type RoleAssignmentContentProps = {
  clinics: AgencyClinicResponse[];
  onClose: () => void;
  assignment: RoleAssignmentDialogState;
  hideFooter?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  clinicDisciplines?: ClinicDisciplinesControl;
  /**
   * Show only the agency roles or only the clinic roles, for a caller that
   * puts each in its own tab. Omitted, both show, in tabs of their own.
   */
  view?: 'agency' | 'clinic';
};

export const RoleAssignmentContent = React.memo(
  React.forwardRef<RoleAssignmentHandle, RoleAssignmentContentProps>(
    function RoleAssignmentContent(
      {
        clinics,
        onClose,
        assignment,
        hideFooter,
        onLoadingChange,
        clinicDisciplines,
        view,
      },
      ref
    ) {
      const {
        agencyRoles,
        clinicRoles,
        isLoading,
        isSaving,
        agencySelection,
        clinicSelection,
        toggleAgencyRole,
        toggleClinicForRole,
        selectAllClinicsForRole,
        clearClinicsForRole,
        handleSave,
      } = assignment;

      const { hasFeatureFlag } = useStore(
        useShallow(state => ({ hasFeatureFlag: state.hasFeatureFlag }))
      );
      const showEmr = useShowEmrUserManagement();
      // Clinic roles decide who can be scheduled for a visit, which an agency
      // with no EMR never does, so the tab goes even where the flag is on.
      const showClinicRoles =
        hasFeatureFlag(FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER) && showEmr;

      // Display only. The selection map behind it still carries every role the
      // member holds, which is what keeps a hidden one from being dropped by the
      // save -- see the seeding in useRoleAssignmentDialog.
      const visibleAgencyRoles = useMemo(
        () => (showEmr ? agencyRoles : filterNonEmrAgencyRoles(agencyRoles)),
        [agencyRoles, showEmr]
      );

      const selectedAgencyCount = useMemo(
        () => [...agencySelection.values()].filter(Boolean).length,
        [agencySelection]
      );

      const selectedClinicRoleCount = useMemo(
        () => [...clinicSelection.values()].filter(s => s.size > 0).length,
        [clinicSelection]
      );

      const rolePickerClinics = clinics;

      useEffect(() => {
        onLoadingChange?.(isLoading);
      }, [isLoading, onLoadingChange]);

      useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

      const agencyRoleGrid =
        visibleAgencyRoles.length === 0 ? (
          <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
            <ShieldCheck className='h-8 w-8 text-muted-foreground/40' />
            <p className='text-sm text-muted-foreground'>
              No agency-level roles available.
            </p>
          </div>
        ) : (
          <div className='grid gap-2 pb-2 pr-1 sm:grid-cols-2 lg:grid-cols-3'>
            {visibleAgencyRoles.map(role => (
              <AgencyRoleCard
                key={role.id}
                role={role}
                selected={!!agencySelection.get(role.id)}
                onToggle={toggleAgencyRole}
              />
            ))}
          </div>
        );

      const clinicRoleList =
        clinicRoles.length === 0 ? (
          <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
            <Building2 className='h-8 w-8 text-muted-foreground/40' />
            <p className='text-sm text-muted-foreground'>
              No clinic-level roles available.
            </p>
          </div>
        ) : (
          <div className='flex flex-col gap-2 pb-2 pr-1'>
            {clinicRoles.map(role => (
              <ClinicRoleSection
                key={role.id}
                role={role}
                clinics={rolePickerClinics}
                selectedClinicIds={clinicSelection.get(role.id) ?? new Set()}
                onToggleClinic={toggleClinicForRole}
                onSelectAll={selectAllClinicsForRole}
                onClear={clearClinicsForRole}
                clinicDisciplines={clinicDisciplines}
              />
            ))}
          </div>
        );

      if (view) {
        if (isLoading) {
          return (
            <div className='flex flex-1 items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          );
        }
        return view === 'agency' ? agencyRoleGrid : clinicRoleList;
      }

      return (
        <>
          {/* Role content */}
          {isLoading ? (
            <div className='flex flex-1 items-center justify-center py-12'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          ) : showClinicRoles ? (
            <Tabs defaultValue='agency' className='flex flex-col'>
              <TabsList className='mx-0 w-fit'>
                <TabsTrigger value='agency' className='gap-1.5'>
                  Agency Roles
                  {selectedAgencyCount > 0 && (
                    <Badge
                      variant='default'
                      className='h-4 min-w-4 px-1 text-[10px]'
                    >
                      {selectedAgencyCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value='clinic' className='gap-1.5'>
                  Clinic Roles
                  {selectedClinicRoleCount > 0 && (
                    <Badge
                      variant='default'
                      className='h-4 min-w-4 px-1 text-[10px]'
                    >
                      {selectedClinicRoleCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Agency roles tab */}
              <TabsContent
                value='agency'
                className='mt-4 focus-visible:outline-none'
              >
                {visibleAgencyRoles.length === 0 ? (
                  <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                    <ShieldCheck className='h-8 w-8 text-muted-foreground/40' />
                    <p className='text-sm text-muted-foreground'>
                      No agency-level roles available.
                    </p>
                  </div>
                ) : (
                  <div className='grid grid-cols-2 gap-3 pb-2 pr-1 sm:grid-cols-2'>
                    {visibleAgencyRoles.map(role => (
                      <AgencyRoleCard
                        key={role.id}
                        role={role}
                        selected={!!agencySelection.get(role.id)}
                        onToggle={toggleAgencyRole}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Clinic roles tab */}
              <TabsContent
                value='clinic'
                className='mt-4 focus-visible:outline-none'
              >
                {clinicRoles.length === 0 ? (
                  <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                    <Building2 className='h-8 w-8 text-muted-foreground/40' />
                    <p className='text-sm text-muted-foreground'>
                      No clinic-level roles available.
                    </p>
                  </div>
                ) : (
                  <div className='flex flex-col gap-3 pb-2 pr-1'>
                    {clinicRoles.map(role => (
                      <ClinicRoleSection
                        key={role.id}
                        role={role}
                        clinics={rolePickerClinics}
                        selectedClinicIds={
                          clinicSelection.get(role.id) ?? new Set()
                        }
                        onToggleClinic={toggleClinicForRole}
                        onSelectAll={selectAllClinicsForRole}
                        onClear={clearClinicsForRole}
                        clinicDisciplines={clinicDisciplines}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            /* Agency roles only — no tabs */
            <div className='focus-visible:outline-none'>
              {visibleAgencyRoles.length === 0 ? (
                <div className='flex flex-col items-center justify-center gap-2 py-12 text-center'>
                  <ShieldCheck className='h-8 w-8 text-muted-foreground/40' />
                  <p className='text-sm text-muted-foreground'>
                    No agency-level roles available.
                  </p>
                </div>
              ) : (
                <div className='grid grid-cols-2 gap-3 pb-2 pr-1 sm:grid-cols-2'>
                  {visibleAgencyRoles.map(role => (
                    <AgencyRoleCard
                      key={role.id}
                      role={role}
                      selected={!!agencySelection.get(role.id)}
                      onToggle={toggleAgencyRole}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!hideFooter && <Separator />}

          {!hideFooter && (
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={handleSave}
                disabled={isSaving || isLoading}
              >
                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save Changes
              </Button>
            </DialogFooter>
          )}
        </>
      );
    }
  )
);
